import { amountToWordsOrEmpty } from '@/shared/utils/numberToWords';
import { formatNumberWithComma } from '@/utils/formula/CodeWindow';
import React, { forwardRef } from 'react';

const BLANK = '.'.repeat(60);

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

const money = (v) => (num(v) ? formatNumberWithComma(num(v)) : '');

const wt = (v) => (num(v) ? num(v).toFixed(3) : '');

const Blank = ({ width = 120, value }) => (
  <span
    style={{
      display: 'inline-block',
      minWidth: width,
      borderBottom: '1px dotted #000',
      textAlign: 'center',
      lineHeight: '14px',
      padding: '0 4px',
    }}
  >
    {value || ' '}
  </span>
);

const cell = {
  border: '1px solid #000',
  padding: '2px 4px',
  fontSize: 10,
  verticalAlign: 'top',
};

const headCell = {
  ...cell,
  fontWeight: 600,
  textAlign: 'center',
  verticalAlign: 'middle',
};

const PURCHASE_COLUMNS = [
  { title: 'क्र.सं.', width: 34, align: 'center' },
  { title: 'गहनाको विवरण', width: 'auto', align: 'left' },
  { title: 'तौल ग्राम', width: 62, align: 'right' },
  { title: 'रिफाईन %', width: 58, align: 'right' },
  { title: 'बाँकी तौल ग्राम', width: 68, align: 'right' },
  { title: 'खरिद दर', width: 78, align: 'right' },
  { title: 'रकम', width: 92, align: 'right' },
];

const SALE_COLUMNS = [
  { title: 'क्र. सं.', width: 44, align: 'center' },
  { title: 'विवरण', width: 'auto', align: 'left' },
  { title: 'सुनको तौल', width: 100, align: 'right' },
  { title: 'दर', width: 100, align: 'right' },
  { title: 'रकम रु.', width: 110, align: 'right' },
];

const blankRows = (count, columns) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={`blank-${i}`}>
      {columns.map((c) => (
        <td key={c.title} style={{ ...cell, height: 17 }}>
          &nbsp;
        </td>
      ))}
    </tr>
  ));

const GahanaPurchaseReceipt = forwardRef(
  ({ business, purchase, customer, receiptNo, date, paymentMode }, ref) => {
    const rows = purchase?.rows || [];
    const total = rows.reduce((a, r) => a + num(r.amount), 0);
    const minRows = Math.max(0, 3 - rows.length);

    return (
      <div
        ref={ref}
        style={{
          width: '210mm',
          height: '297mm',
          boxSizing: 'border-box',
          padding: '8mm 10mm',
          background: '#fff',
          color: '#000',
          fontFamily: '"Noto Sans Devanagari", "Mangal", sans-serif',
          fontSize: 11,
          overflow: 'hidden',
        }}
      >
        <style>{'@page { size: A4 portrait; margin: 0; }'}</style>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ width: 130, fontSize: 10, paddingTop: 14 }}>
            भर्पाई नं. <Blank width={70} value={receiptNo} />
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 12 }}>काठमाडौँ ज्वैलरी</div>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>
              {business?.name}
            </div>
            <div style={{ fontSize: 11 }}>{business?.address}</div>
            <div style={{ fontSize: 11 }}>
              (पान नं. {business?.panNumber || ''})
            </div>
          </div>
          <div style={{ width: 130 }} />
        </div>

        <div style={{ textAlign: 'center', margin: '4px 0 6px' }}>
          <span
            style={{
              border: '1.5px solid #000',
              padding: '2px 14px',
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            गहना खरिद भपाई
          </span>
        </div>

        <div style={{ fontSize: 10, lineHeight: '18px', textAlign: 'justify' }}>
          म बिक्रेता स्थायी{' '}
          <Blank width={110} value={customer?.permanentTole} /> प्रदेश{' '}
          <Blank width={90} value={customer?.permanentProvince} /> जिल्ला{' '}
          <Blank width={90} value={customer?.permanentDistrict} /> म.न.पा. वडा
          नं. <Blank width={40} value={customer?.permanentWardNo} /> हाल अस्थायी{' '}
          <Blank width={110} value={customer?.temporaryTole} /> प्रदेश{' '}
          <Blank width={90} value={customer?.temporaryProvince} /> जिल्ला{' '}
          <Blank width={90} value={customer?.temporaryDistrict} /> म.न.पा. वडा
          नं. <Blank width={40} value={customer?.temporaryWardNo} /> मा बस्ने
          श्री <Blank width={140} value={customer?.fatherName} /> को छोरा/छोरी
          बर्ष <Blank width={40} /> को श्री/सुश्री/श्रीमती{' '}
          <Blank width={150} value={customer?.name} /> को नाति/नातिनी श्री{' '}
          <Blank width={140} value={customer?.grandfatherName} /> को हक लाग्ने
          निम्न बमोजिमका सुनको गरगहना आफ्नो ईच्छाले माथी उल्लेखित पसलमा आजको
          दरभाउ अनुसार उचित ढंगले लेनदेन गरेको साँचो हो, झुठ्ठा ठहरे वा कुनै
          दाबी विरोध पर्न आएमा म बिक्रेता स्वयं जबाफदेही हुनेछु भनी मन्जुरी सहित
          खरिद भर्पाइमा सहिछाप गरेको छु।
        </div>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: 6,
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              {PURCHASE_COLUMNS.map((c) => (
                <th
                  key={c.title}
                  style={{
                    ...headCell,
                    width: c.width === 'auto' ? undefined : c.width,
                  }}
                >
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id || i}>
                <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
                <td style={cell}>{row.itemName}</td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {wt(row.grossWeight)}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {num(row.purity) ? num(row.purity).toFixed(2) : ''}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {wt(row.pureWeight)}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {money(row.rate)}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {money(row.amount)}
                </td>
              </tr>
            ))}
            {blankRows(minRows, PURCHASE_COLUMNS)}
            <tr>
              <td colSpan={5} style={{ ...cell, border: 'none' }} />
              <td style={{ ...headCell, textAlign: 'right' }}>जम्मा</td>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>
                {money(total)}
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ fontSize: 10, marginTop: 3 }}>
          अक्षरेपी रू. {amountToWordsOrEmpty(total) || BLANK}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 6,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: 1, fontSize: 10, lineHeight: '20px' }}>
            <div>
              बिक्रेताको नाम : <Blank width={150} value={customer?.name} />
            </div>
            <div>
              ठेगानाः <Blank width={160} value={customer?.address} />
            </div>
            <div>
              फोन नं.: <Blank width={160} value={customer?.phone} />
            </div>
            <div>
              हस्ताक्षरः <Blank width={155} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
            <div style={{ width: 68, height: 74, border: '1px solid #000' }} />
            <div style={{ width: 68, height: 74, border: '1px solid #000' }} />
          </div>
          <div style={{ flex: 1, fontSize: 10, lineHeight: '20px' }}>
            <div>
              क्रेताको नाम : <Blank width={150} value={business?.name} />
            </div>
            <div>
              हस्ताक्षरः <Blank width={155} />
            </div>
          </div>
        </div>

        <div style={{ fontSize: 10, marginTop: 4 }}>
          भुक्तानीको किसिमः नगद / क्यू आर / IPS वा RTGS / चेक नं.{' '}
          <Blank width={220} value={paymentMode} />
        </div>
        <div style={{ fontSize: 10, marginTop: 2, paddingLeft: 28 }}>
          ➡ संलग्नः– सक्कल बिल / परिचय खोल्ने कागजात <Blank width={230} />
        </div>

        <div
          style={{
            borderTop: '1px dashed #000',
            margin: '8px 0 6px',
          }}
        />

        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, fontSize: 11 }}>
            भर्पाइ नं. <Blank width={110} value={receiptNo} />
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 13 }}>ग्राहक प्रति</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              गहना बिक्री भपाई
            </div>
          </div>
          <div style={{ flex: 1, fontSize: 11, textAlign: 'right' }}>
            मिति : <Blank width={110} value={date} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, fontSize: 11, marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            नाम : <Blank width={200} value={customer?.name} />
          </div>
          <div style={{ flex: 1 }}>
            ठेगाना : <Blank width={200} value={customer?.address} />
          </div>
        </div>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: 6,
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              {SALE_COLUMNS.map((c) => (
                <th
                  key={c.title}
                  style={{
                    ...headCell,
                    width: c.width === 'auto' ? undefined : c.width,
                  }}
                >
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`sale-${row.id || i}`}>
                <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
                <td style={cell}>{row.itemName}</td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {wt(row.pureWeight || row.grossWeight)}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {money(row.rate)}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {money(row.amount)}
                </td>
              </tr>
            ))}
            {blankRows(minRows, SALE_COLUMNS)}
            <tr>
              <td colSpan={3} style={{ ...cell, border: 'none' }} />
              <td style={{ ...headCell, textAlign: 'right' }}>जम्मा</td>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>
                {money(total)}
              </td>
            </tr>
          </tbody>
        </table>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 20,
            marginTop: 6,
          }}
        >
          <div style={{ flex: 1, fontSize: 11, lineHeight: '18px' }}>
            <div style={{ fontWeight: 600 }}>पसलको नाम / ठेगाना</div>
            <div style={{ fontWeight: 700 }}>{business?.name}</div>
            <div>{business?.address}</div>
            <div>पान नं. {business?.panNumber || ''}</div>
          </div>
          <div style={{ fontSize: 11 }}>
            हस्ताक्षर : <Blank width={130} />
          </div>
          <div style={{ fontSize: 11 }}>लोगो / छाप</div>
        </div>

        <div style={{ fontSize: 10, marginTop: 4 }}>
          भुक्तानीको किसिमः नगद / क्यू आर / IPS वा RTGS / चेक नं.{' '}
          <Blank width={220} value={paymentMode} />
        </div>
      </div>
    );
  },
);

GahanaPurchaseReceipt.displayName = 'GahanaPurchaseReceipt';

export default GahanaPurchaseReceipt;
